import { useAppForm } from '@/components/form/formHooks';
import {
  MarkierungType,
  markierungTypes,
} from '@/domain/termine/shared/types/Markierung';
import { useToast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import z from 'zod';
import { useUpdateTerminKalender } from '@/domain/termine/hooks/useUpdateTerminKalender';
import { TerminKalender } from '@/domain/termine/shared/types';

type FormUpdateValues = {
  idTerminkalender: string;
  titel: string;
  markierung: MarkierungType;
};

const createValidationSchema = (t: TFunction) =>
  z.object({
    idTerminkalender: z
      .string()
      .min(1, t('domain.termine.sheet.validation.titel')),
    titel: z.string(),
    markierung: z.enum(markierungTypes),
  });

export function useUpdateTerminKalenderForm(
  terminKalender: TerminKalender | undefined,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { updateTerminKalender } = useUpdateTerminKalender();

  if (!terminKalender) {
    throw new Error('Update des Terminkalenders nicht möglich.');
  }

  const defaultValues: FormUpdateValues = {
    idTerminkalender: terminKalender.id,
    titel: terminKalender.name,
    markierung: terminKalender.markierung ?? markierungTypes[19],
  };

  return useAppForm({
    defaultValues: defaultValues,
    validators: {
      onBlur: createValidationSchema(t),
    },
    onSubmit: async ({ value }) => {
      try {
        const response = await updateTerminKalender({
          idTerminkalender: value.idTerminkalender,
          markierung: value.markierung,
          name: value.titel,
        });

        if (!response) {
          throw Error('No response from createTerminKalender mutation');
        }

        if (response?.error) {
          toast({
            variant: 'destructive',
            title: t('domain.termine.kalender.errorCreatedTerminCalendar'),
            description: response.error.message,
          });
          return;
        }

        toast({
          title: t('domain.termine.kalender.createdTerminCalendar', {
            name: value.titel,
          }),
        });
      } catch (error) {
        Sentry.captureException(error);
        toast({
          variant: 'destructive',
          title: t('domain.termine.kalender.errorCreatedTerminCalendar'),
        });
        throw Error(error instanceof Error ? error.message : String(error));
      }
    },
  });
}
